import React, { useEffect, useContext, useState, useRef }  from 'react'
import { View, ScrollView, StyleSheet, Platform } from 'react-native'
import { Button, Text, Icon, Datepicker } from '@ui-kitten/components'
import { UserContext } from '../../global/UserProvider'
import ProductSticker from '../../components/ProductSticker'
import StyledTextInput from '../../components/StyledTextInput'
import { useReactToPrint } from 'react-to-print'
import HeaderIconButton from '../../components/HeaderIconButton'
import { format } from 'date-fns'
import { composeStickerBarcode } from '../../utils/stickerBarcode'

/**
 * ATL-1264: the barcode fields of one sticker, composed in ONE place.
 *
 * The bulk-selection path and the one-at-a-time path each carried their own copy of this rule,
 * and the copies drifted: bulk appended the date+weight suffix to every code, single appended it
 * only to six-digit internal ones. The same 12-digit product therefore printed when added singly
 * and printed blank when selected in bulk. `barcodeFormat` travels with the value because only
 * the composer knows whether it extended the code — see utils/stickerBarcode.js.
 *
 * `stickerDate` is the date the SHEET is printed for, not today: the two compose paths passed
 * `new Date()` while the weight editor passed the screen's chosen `date`, so editing a weight
 * silently rewrote the day segment of a sheet dated for tomorrow. One date, one place.
 */
const stickerBarcodeFields = (rawBarcode, stickerDate, weightSegment = '000') => {
  const composed = composeStickerBarcode({
    barcode: rawBarcode,
    dayOfYear: format(stickerDate, 'DDD'),
    weightSegment,
  })
  return {
    barcode: composed?.value ?? '',
    barcodeFormat: composed?.format ?? 'CODE128',
    originalBarcode: rawBarcode == null ? '' : rawBarcode.toString(),
  }
}
import AsyncStorage from '@react-native-async-storage/async-storage'
import { useTranslation } from 'react-i18next'
import { useAtomValue } from 'jotai'
import { useToast } from 'react-native-toast-notifications'
import * as Sentry from '@sentry/react-native'
import { currencyFormatterAtom, localeDateServiceAtom, productUnitsDictAtom, priceTypesAtom } from '../../global/AtomsProvider'
import { getProductPrice } from '../../utils/priceTypeHelpers'

// Sentry gives a stackless "Non-Error exception captured" event for anything that
// isn't an Error, so normalize before reporting.
const toError = (value) => (value instanceof Error ? value : new Error(String(value)))

export default function PrintStickersScreen( { navigation, route } ) {

  const { products } = route.params
  const productUnitsDict = useAtomValue(productUnitsDictAtom)
  const localeDateService = useAtomValue(localeDateServiceAtom)
  const { t } = useTranslation()
  const priceTypes = useAtomValue(priceTypesAtom)
  const toast = useToast()

  const [context, setContext] = useContext(UserContext)
  const currencyFormatter = useAtomValue(currencyFormatterAtom)
  const [date, setDate] = useState(new Date())
  const [stickers, setStickers] = useState([])
  const [stickerSettings, setStickerSettings] = useState()
  const [isInitialized, setIsInitialized] = useState(false)
  const hasUpdatedStickers = useRef(false)
  // Latch so the empty-sheet report below fires at most once per mount — the
  // toast invites re-tapping, and N taps must not become N Sentry events.
  const hasReportedEmptySheet = useRef(false)
  const printComponentRef = useRef()
  const _rangePicker = React.createRef()

  const getAdditionalPrices = (product, settings) => {
    // If no settings or no selected price types, return empty array
    if (!settings?.selectedPriceTypes || settings.selectedPriceTypes.length === 0) {
      return []
    }
    
    // If priceTypes not loaded yet, return empty array
    if (!priceTypes || priceTypes.length === 0) {
      return []
    }
    
    // Filter to only show selected price types that have prices
    return priceTypes
      .filter(pt => 
        settings.selectedPriceTypes.includes(pt.id) && 
        getProductPrice(product, context.user.store?.id, pt.id) > 0
      )
      .map(pt => ({
        name: pt.name,
        price: currencyFormatter.format(getProductPrice(product, context.user.store?.id, pt.id))
      }))
  }

  const populateInitialProducts = (settings) => {
    var newProducts = []
    if (products) {
      for (const [index, value] of products.entries()) {
        const defaultPrice = getProductPrice(value, context.user.store?.id, null) || value.salePrices?.[context.user.store?.id] || 0
        
        // Get selected additional price types for this product
        const additionalPriceTypes = getAdditionalPrices(value, settings)
        
        if (!value.type) {
          for (var i = 0; i < value.quantity; i++) { 
            newProducts.push({
              name: value.name,
              ...stickerBarcodeFields(value.barcode, date),
              price: defaultPrice,
              salePrice: defaultPrice,
              additionalPrices: additionalPriceTypes,
              weight: null,
              type: value.type,
              product: value
            })
          }
        } else {
          newProducts.push({
            name: value.name,
            ...stickerBarcodeFields(value.barcode, date),
            price: defaultPrice,
            salePrice: defaultPrice,
            additionalPrices: additionalPriceTypes,
            weight: 0,
            type: value.type,
            product: value
          })
        }
      }
    }
    return newProducts
  }

  useEffect(() => {
    getPrinterSettings()
  }, [])

  // Populate stickers after settings and price types are loaded (only once on init)
  useEffect(() => {
    if (!stickerSettings || isInitialized) {
      return
    }
    
    // If user has selected price types, wait for priceTypes to load
    const needsPriceTypes = stickerSettings.selectedPriceTypes && stickerSettings.selectedPriceTypes.length > 0
    const priceTypesReady = !needsPriceTypes || (priceTypes && priceTypes.length > 0)
    
    if (priceTypesReady) {
      const initialProducts = populateInitialProducts(stickerSettings)
      setStickers(initialProducts)
      setIsInitialized(true)
    }
  }, [stickerSettings, priceTypes, isInitialized])

  // Update existing stickers when settings change (for stickers added before settings loaded)
  useEffect(() => {
    if (stickerSettings && stickers.length > 0 && priceTypes && priceTypes.length > 0 && !hasUpdatedStickers.current) {
      const updatedStickers = stickers.map(sticker => {
        // Only update if this sticker has no additional prices or they're empty
        if (!sticker.additionalPrices || sticker.additionalPrices.length === 0) {
          const additionalPrices = getAdditionalPrices(sticker.product, stickerSettings)
          if (additionalPrices.length > 0) {
            return { ...sticker, additionalPrices }
          }
        }
        return sticker
      })
      
      // Only update if something actually changed
      const hasChanges = updatedStickers.some((sticker, idx) => 
        sticker.additionalPrices !== stickers[idx].additionalPrices
      )
      
      if (hasChanges) {
        setStickers(updatedStickers)
        hasUpdatedStickers.current = true
      }
    }
  }, [stickerSettings, priceTypes, stickers])

  useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <HeaderIconButton
          onPress={() => {
            if (Platform.OS === 'web') {
              navigation.navigate('AddProductSticker', {addSticker})
            } else {
              alert(t('printStickers.worksOnWebError'))
            }
            
          }}
          iconName='plus-outline'
        />
      ),
    })
  }, [stickers.length])

  const addSticker = (item, index) => {
    const defaultPrice = getProductPrice(item, context.user.store?.id, null) || item.salePrices?.[context.user.store?.id] || 0
    
    // Use current settings or default settings if not loaded yet
    const currentSettings = stickerSettings || {
      stickerHeight: 112,
      showDate: true,
      generalFontSize: 10,
      priceFontSize: 12,
      selectedPriceTypes: []
    }
    
    // Get selected additional price types for this product
    const additionalPriceTypes = getAdditionalPrices(item, currentSettings)
    
    var newStickers = [...stickers]
    newStickers.push({
      ...item, 
      salePrice: defaultPrice, 
      price: defaultPrice, 
      additionalPrices: additionalPriceTypes,
      ...stickerBarcodeFields(item.barcode, date),
      product: item
    })
    setStickers(newStickers)
  }

  const getPrinterSettings = async () => {
    let stickerSettings = await AsyncStorage.getItem('stickerPrinterSettings')
    if (stickerSettings) {
      const parsed = JSON.parse(stickerSettings)
      // Ensure backwards compatibility - add selectedPriceTypes if missing
      if (!parsed.selectedPriceTypes) {
        parsed.selectedPriceTypes = []
      }
      setStickerSettings(parsed)
    } else {
      const defaultSettings = {
        stickerHeight: 112,
        showDate: true,
        generalFontSize: 10,
        priceFontSize: 12,
        selectedPriceTypes: []
      }
      AsyncStorage.setItem('stickerPrinterSettings', JSON.stringify(defaultSettings))
      setStickerSettings(defaultSettings)
    }
  }

  const saveStickerPrinterSettings = async (settings) => {
    await AsyncStorage.setItem('stickerPrinterSettings', JSON.stringify(settings))
    setStickerSettings(settings)
    // Refresh stickers with new settings
    const updatedStickers = stickers.map(sticker => ({
      ...sticker,
      additionalPrices: getAdditionalPrices(sticker.product, settings)
    }))
    setStickers(updatedStickers)
  }

  // Instrumentation only — this does NOT change what or how we print (ATL-722).
  //
  // Until now this path had no error surface at all: `react-to-print` only console.error's,
  // so a failed label print produced no toast, no Sentry event, and no breadcrumb. That is
  // why a customer-reported failure could not be diagnosed from our side and had to be
  // triaged from a photograph of a monitor. Everything below exists to turn the NEXT
  // failure into evidence.
  const printDiagnostics = () => ({
    stickerCount: stickers.length,
    // Distinguishes the supply/приемка entry point (many labels at once) from the Home
    // entry point (labels added one at a time) — the two behave differently in practice.
    enteredFromSupply: Boolean(products),
    sourceProductCount: products ? products.length : 0,
    platform: Platform.OS,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    stickerHeight: stickerSettings?.stickerHeight ?? null,
  })

  const reportPrintFailure = (errorLocation, error) => {
    Sentry.captureException(toError(error), {
      tags: { feature: 'print_stickers', print_error_location: errorLocation },
      extra: printDiagnostics(),
    })
    toast?.show(t('printStickers.printFailed'), {
      type: 'danger',
      placement: 'bottom',
      duration: 4000,
    })
  }

  const printStickers = useReactToPrint({
    contentRef: printComponentRef,
    onPrintError: reportPrintFailure,
    // `onPrintError` is ONLY reachable when either `onBeforePrint` or `print` is
    // supplied — verified in react-to-print@3.3.0: the two call sites are
    // `h(e).then(a).catch(c => y?.("print", …))` (gated on `print`) and
    // `a().catch(i => c?.("onBeforePrint", …))` (gated on `onBeforePrint`).
    // Without this option every real failure goes to console.error only and the
    // handler above is dead code. So we supply `print` and reproduce the library's
    // own default behaviour, with the failures turned into rejections.
    print: (target) =>
      new Promise((resolve, reject) => {
        const printWindow = target.contentWindow
        if (!printWindow) {
          // Defensive only: the library calls this from inside its own
          // `if (e.contentWindow)` guard, so this cannot fire today. (Its own
          // contentWindow-failed path stays console-only and out of our reach.)
          reject(new Error('Print iframe has no contentWindow'))
          return
        }
        if (typeof printWindow.print !== 'function') {
          reject(new Error('This browser cannot print iframes'))
          return
        }
        try {
          printWindow.print()
        } catch (error) {
          // A browser refusing the print call (Safari blocks prints it considers
          // automatic) lands here — previously invisible.
          reject(toError(error))
          return
        }
        // The library defers its own teardown by 500ms on mobile user agents;
        // resolving immediately would tear the iframe down before printing
        // finishes. Matching it unconditionally only ever delays desktop
        // teardown — never tears down earlier than before.
        setTimeout(resolve, 500)
      }),
  })

  const handlePrint = () => {
    if (stickers.length === 0) {
      // An empty sheet is normally just a user pressing Print before adding a
      // label, or after deleting them all — user behaviour, not a defect.
      // Reporting it unconditionally would repeat ATL-562, where benign print
      // events flooded Sentry. Only one empty case is worth evidence: we arrived
      // from приемка WITH products and still rendered nothing — the "prints
      // blank" symptom. Even that is reachable benignly (delete every row from a
      // supply-sourced sheet), so it is latched to one event per mount rather
      // than one per tap.
      if (products && products.length > 0 && !hasReportedEmptySheet.current) {
        hasReportedEmptySheet.current = true
        Sentry.captureMessage('Print stickers: entered from supply with products but no stickers rendered', {
          level: 'warning',
          tags: { feature: 'print_stickers' },
          extra: printDiagnostics(),
        })
      }
      toast?.show(t('printStickers.nothingToPrint'), {
        type: 'warning',
        placement: 'bottom',
        duration: 4000,
      })
      return
    }

    // `react-to-print` bails with a console-only "There is nothing to print" when
    // the content node is missing, which onPrintError never sees. Catch it here.
    if (!printComponentRef.current) {
      reportPrintFailure('content', new Error('Print content node is not mounted'))
      return
    }

    Sentry.addBreadcrumb({
      category: 'print',
      message: 'print_stickers.invoked',
      level: 'info',
      data: printDiagnostics(),
    })

    try {
      printStickers()
    } catch (error) {
      // Guards the synchronous portion only (iframe setup). The print call itself
      // happens later inside a setTimeout, which is why the `print` option above
      // exists — that is what routes a real print failure to onPrintError.
      reportPrintFailure('invoke', error)
    }
  }

  const setItemWeight = (index, text) => {
    var newArr = [...stickers]
    var oldItem = newArr[index]
    var formattedWeight = ''
    if (text.length > 0) {
      const weight = parseFloat(text)
      let roundedWeight = parseFloat(weight?.toFixed(2))
      let newPrice = oldItem.salePrice * roundedWeight
      if (weight < 10) {
        formattedWeight = weight?.toFixed(2).replace('.', '')
      } else {
        formattedWeight = '000'
      }
      let newItem = {...oldItem, weight: text, roundedWeight, price: newPrice?.toFixed(0),
        ...stickerBarcodeFields(oldItem.originalBarcode, date, formattedWeight)}
      newArr[index] = newItem
      setStickers(newArr)
    } else {
      formattedWeight = '000'
      let newItem = {...oldItem, weight: text, roundedWeight: 0, price: 0,
        ...stickerBarcodeFields(oldItem.originalBarcode, date, formattedWeight)}
      newArr[index] = newItem
      setStickers(newArr)
    }
  }

  const navigateToSettings = () => {
    navigation.navigate('StickerPrinterSettings', {initialSettings: stickerSettings, saveStickerPrinterSettings})
  }
  
  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      <View>
        { stickerSettings?.showDate && (
          <>
            <Text category='label' style={styles.label}>
              {t('printStickers.stickerDate')}
            </Text>
            <Datepicker
              placement='top'
              min={new Date('2000-12-01')}
              max={new Date('2029-12-31')}
              dateService={localeDateService}
              date={date}
              ref={_rangePicker}
              onSelect={(date) => {
                setDate(date)
                _rangePicker.current.blur()
              }}
            />
          </>
        )}
        
        <Text category='label' style={styles.label}>
          {t('printStickers.products')}
        </Text>
        {stickers.map((item, index) => (
          <View key={item+index} style={styles.row}>
            <Text style={styles.name}>{index + '. ' + item.name}</Text>
            {item.type ? (
              <View style={styles.weightView}>
                <Text>{t('printStickers.weight')}</Text>
                <StyledTextInput 
                  value={item.weight}
                  onChangeText={text => setItemWeight(index, text.replace(/,/g, '.'))}
                  keyboardType='decimal-pad'
                  style={styles.weightField}
                  returnKeyType='done'
                />
              </View>
            ) : (
              <Text>{1 + ' ' + (productUnitsDict[item.productUnit]?.shortName || t('pcs'))}</Text>
            )}
            <Button 
              appearance='ghost' accessoryLeft={(style) => (
                <Icon {...style} name='copy-outline'/>
              )} onPress={() => {
                var newStickers = [...stickers]
                newStickers.splice(index, 0, item)
                setStickers(newStickers)
              }} 
            />
            <Button 
              appearance='ghost' status='danger' accessoryLeft={(style) => (
                <Icon {...style} name='trash-outline'/>
              )} onPress={() => {
                var newStickers = [...stickers]
                newStickers.splice(index, 1)
                setStickers(newStickers)
              }} 
            />
          </View>
        ))}
      </View>
      <Button style={styles.printButton} status='basic' onPress={navigateToSettings}>{t('printStickers.stickerSettings')}</Button>
      <Button style={styles.printButton} onPress={Platform.OS === 'web' ? handlePrint : () => {alert(t('printStickers.worksOnWebError'))}}>{t('printStickers.printStickersCTA')}</Button>
      <Text style={styles.printButton} category='h3'>{t('printStickers.preview')}</Text>
      <View ref={printComponentRef}>
        {stickers.map((item, index) => (
            <ProductSticker key={item+index} 
              height={stickerSettings?.stickerHeight} name={item.name} barcode={item.barcode} 
              date={stickerSettings?.showDate ? date : null} weight={item.roundedWeight} 
              price={currencyFormatter.format(item.price)} 
              additionalPrices={item.additionalPrices || []}
              marginVertical={0} 
              generalFontSize={stickerSettings?.generalFontSize} priceFontSize={stickerSettings?.priceFontSize}
              barcodeFormat={item.barcodeFormat}
            />
        ))}
      </View>
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    padding: 20,
    paddingBottom: 40,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    margin: 10,
  },
  name: {
    width: 200,
  },
  weightView: {
    marginRight: 10,
    marginBottom: 20,
  },
  printButton: {
    marginBottom: 10,
  },
  label: {
    marginTop: 10,
    marginBottom: 5
  }
})